import { useCallback, useEffect, useState, type ReactNode } from "react";

import {
  blurAndGlareNote,
  captureRows,
  captureState,
  JOB_STATUS_SCOPE,
  KEEP_THE_APP_OPEN,
  landedAccordingToUploadService,
  NOTHING_TO_UPLOAD_SUB,
  NOTHING_TO_UPLOAD_TITLE,
  uploadChip,
  uploadChipIsDesigned,
  UPLOADING_TITLE,
  UPLOAD_SELF_SCOPE,
  UPLOAD_SUBJECT_ABSENCE,
  type CaptureState,
} from "../document-upload.ts";
import type { DriverApi, ProcessingJobStatusRecord } from "../driver-api.ts";
import type { QueuedUpload, QueueStore } from "../offline-queue.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { HeroScanFrame } from "./Hero.tsx";
import { PhoneShell } from "./PhoneShell.tsx";
import { StateScreen } from "./StateScreen.tsx";

/**
 * `Document upload` — the photographs leaving this handset, and the check waiting for them.
 *
 * The design: the `scan` hero, the `Offline` pill, the `['unknown','uploading 2 of 3']` chip,
 * _"Uploading your permit"_, _"Keep the app open. On weak signal we resume where we left off."_,
 * three check rows, the dashed `pending` note about blur and glare, and **no buttons** — the one
 * screen of the eighteen the design gives no action at all.
 *
 * `src/document-upload.ts` carries the reasoning for every value, including the two the platform
 * does not publish and which are therefore drawn as absences: the `64%` and the word `permit`.
 *
 * `tests/contract/mobile-screen-support.test.ts` marks this screen **buildable with nothing
 * missing**. It was blocked on FIL-03's job read until that read was published on `/v1` as a
 * narrowed status projection under `media-processing:status:read` — `docs/OPEN_WORK.md` item 2, in
 * the shape ADR 0025 settled for FIL-02 next door.
 *
 * ## Two halves, and this is the second screen whose first half is the handset
 *
 * `6 Offline queue` draws what this phone is holding. So does this, from the same store: the three
 * rows are the upload items in `src/offline-queue.ts`, with their own titles, and the state of each
 * is read from the three fields that record how far it got. There is nothing to intercept because
 * the platform has not been told any of it yet.
 *
 * What is new is the **resume check**. The queue's `written` flag is this handset's record of its
 * own `PUT` succeeding, and a handset killed mid-write has no record of one that did. ADR 0025
 * published `GET /v1/tenants/{tenantId}/uploads/{uploadId}` precisely so a device can ask, and
 * `quarantined` is FIL-02's own word for "the bytes are in". So an item the queue thinks is still in
 * flight is asked about, and an item FIL-02 says landed is drawn as landed — which is what makes the
 * design's _"we resume where we left off"_ a sentence this build earned rather than printed.
 *
 * The second half is FIL-03's, and it is one read: the note. A policy printed without checking is a
 * claim, so the design's sentence is drawn when FIL-03 confirms the check is queued or running and
 * says something else when it says the check failed.
 */

export interface DocumentUploadProps {
  readonly api: DriverApi;
  readonly tenantId: string;
  /** This phone's queue, which is where the three captures live. */
  readonly store: QueueStore;
  /**
   * The FIL-03 job this capture set is being checked by, from the address.
   *
   * Told rather than discovered, and for the same reason `8 Proof`'s proof is: FIL-03 publishes no
   * listing on either surface, and its `jobId` is derived inside the service from the quarantine
   * event's own identifier — `deterministicJobId(tenantId, eventId, transformation)` — which no
   * handset sees. A notification through NTF would open the application at `?job=<id>`. Recorded as
   * Q-142; absent is a state rather than an error.
   */
  readonly jobId: string | undefined;
  /**
   * The scopes this sign-in carries, so a missing one is named rather than discovered as a 403.
   *
   * The session's own `ReadonlySet` rather than an array, which is what `session.ts` builds from the
   * token's `scope` claim: a screen that took an array would make the router convert at the call
   * site, and a conversion at a call site is where a membership test quietly becomes a substring
   * one.
   */
  readonly scopes: ReadonlySet<string>;
}

interface Loaded {
  readonly captures: readonly { readonly item: QueuedUpload; readonly state: CaptureState }[];
  /** True when an unfinished capture's photograph is not in this session (Q-73). */
  readonly photographsLost: boolean;
  readonly job: ProcessingJobStatusRecord | undefined;
  /** True when FIL-03 was asked and refused, which is not the same as not being asked. */
  readonly jobUnreadable: boolean;
}

export function DocumentUpload(props: DocumentUploadProps): ReactNode {
  const { api, tenantId, store, jobId } = props;
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [state, setState] = useState<ViewState>({ kind: "loading" });

  const load = useCallback(() => {
    setState({ kind: "loading" });
    const items = store.list().filter((item): item is QueuedUpload => item.kind === "upload");

    void (async (): Promise<void> => {
      const captures = await Promise.all(
        items.map(async (item) => {
          const first = captureState(item);
          /*
           * Only an item this handset thinks is mid-flight is asked about.
           *
           * One whose bytes never left has nothing for FIL-02 to have an opinion on, and one this
           * phone already recorded as written is already right. So the resume check costs one
           * request per genuinely uncertain capture rather than one per row.
           */
          if (first !== "sending" || item.uploadId === undefined) return { item, state: first };
          try {
            const upload = await api.uploadState({ tenantId, uploadId: item.uploadId });
            const state: CaptureState = landedAccordingToUploadService(upload) ? "sent" : "sending";
            return { item, state };
          } catch {
            /*
             * A refused or unreachable upload read leaves the handset's own reading standing.
             *
             * Not an error screen: the three rows are device state and are drawable without FIL-02
             * having answered anything. What is lost is only the chance to notice that a capture
             * landed after all, and the row then says `resumes if interrupted`, which stays true.
             */
            return { item, state: first };
          }
        }),
      );
      /*
       * Whether this session can carry any unfinished upload further, asked once.
       *
       * The bytes are held in memory beside the record and are gone after a reload (Q-73), so a
       * courier who came back to this screen has records with nothing behind them. It is a
       * condition on the screen rather than a status on a row — `6 Offline queue` settled that,
       * and `src/document-upload.ts` says why.
       */
      const photographsLost = captures.some(
        (capture) => capture.state !== "sent" && store.bytes(capture.item.id) === undefined,
      );
      if (jobId === undefined) {
        setLoaded({ captures, photographsLost, job: undefined, jobUnreadable: false });
        setState({ kind: "ready" });
        return;
      }
      try {
        const job = await api.mediaProcessingJob({ tenantId, jobId });
        setLoaded({ captures, photographsLost, job, jobUnreadable: false });
      } catch (error: unknown) {
        /*
         * FIL-03's refusal costs the note and nothing else.
         *
         * Caught here rather than by the outer handler, because the three rows are this handset's
         * and drawing an error page over them would hide work a courier can see is in progress. A
         * forbidden read is the exception: a sign-in without the scope is a fact the courier can
         * act on, so it is raised as the screen's state.
         */
        const next = stateForError(error, [JOB_STATUS_SCOPE]);
        if (next.kind === "forbidden") {
          setState(next);
          return;
        }
        setLoaded({ captures, photographsLost, job: undefined, jobUnreadable: true });
      }
      setState({ kind: "ready" });
    })();
  }, [api, tenantId, store, jobId]);

  useEffect(load, [load]);

  if (!props.scopes.has(UPLOAD_SELF_SCOPE)) {
    /*
     * Said before anything is attempted, because this screen's whole subject is uploading.
     *
     * `7 Document expiring` makes the same statement about the same scope at the point of pressing a
     * button; here it is the screen, so a courier is told at the top rather than after watching
     * three rows sit at `Waiting` for ever.
     */
    return (
      <StateScreen
        screen="document-upload"
        state={{ kind: "forbidden", missingScopes: [UPLOAD_SELF_SCOPE] }}
      />
    );
  }

  if (state.kind !== "ready" || loaded === undefined) {
    return (
      <StateScreen
        screen="document-upload"
        state={state}
        onRetry={load}
        purpose="Checking what's uploaded and what the blur and glare check says."
      />
    );
  }

  if (loaded.captures.length === 0) {
    return (
      <StateScreen
        screen="document-upload"
        chip={{ class: "waiting", label: "nothing waiting" }}
        title={NOTHING_TO_UPLOAD_TITLE}
        sub={NOTHING_TO_UPLOAD_SUB}
      />
    );
  }

  const states = loaded.captures.map((capture) => capture.state);
  const note = blurAndGlareNote(loaded.job, loaded.jobUnreadable);
  const designed = uploadChipIsDesigned(states, loaded.photographsLost) && note.designed;

  return (
    <PhoneShell
      screen="document-upload"
      /* `art:'scan'` — the design's bordered card with its barcode, shared with `5 Scan`. */
      art={<HeroScanFrame />}
      /*
       * `on:0`, the design's own flag on this screen.
       *
       * A courier photographing a permit is doing paperwork rather than a shift, and presence is
       * DRV-05's and is not read here — so the pill is drawn from what was approved rather than from
       * a second read composed to answer a pill. The same reading `Vehicle` takes of the same flag.
       */
      online={false}
      earningsLabel={null}
      chip={uploadChip(states, loaded.photographsLost)}
      title={UPLOADING_TITLE}
      titleAbsence={UPLOAD_SUBJECT_ABSENCE}
      sub={KEEP_THE_APP_OPEN}
      checks={captureRows(loaded.captures)}
      note={{ kind: note.kind, text: note.text }}
      /* The design gives this screen no buttons, so it has none. */
      actions={[]}
      {...(designed ? {} : { flag: "wording not in the design" })}
    />
  );
}
